import React, { useEffect, useState } from "react";
import styled, { useTheme } from "styled-components";
import { CheckCircleOutline } from "@mui/icons-material";
import { Box, FormControl, FormControlLabel, Radio, RadioGroup, TextField, Typography } from "@mui/material";
import SmileyRatings from "./smileys";
import { pSBC } from "../../../../../lib/pSBC";
import { useDispatch, useSelector } from "react-redux";
import { setFormFields, setFormState } from "../../../../../redux/actions/formActions";
import useLocalization from "../../../../../hooks/localization";
import { EMAIL_FIELD, RADIO_FIELD, STRING_FIELD, TEXTAREA_FIELD } from "../../../../../constants/form";
import { emailFieldValidator } from "../../../../../lib/form/validators";
import { getChatActions, logEventRecord } from "../../../../../redux/actions/chatActions";
import uuid from "react-uuid";
import { FEEDBACK_SUB_EVENT, FEEDBACK_SUBMIT, RESET_CHAT_EVENT, WIDGET_SUB_EVENT, FEEDBACK_CLOSE } from "../../../../../constants/events";
import { useRef } from "react";
import useFocusTrap from "../../../../common/focusTrap";
const NO_VALIDATOR = () => {};

const FIELDS = {
  [EMAIL_FIELD]: {
    validator: emailFieldValidator,
  },
  [STRING_FIELD]: {
    validator: NO_VALIDATOR,
  },
  [TEXTAREA_FIELD]: {
    validator: (value, field) => {
      if (value?.length < field?.min || value?.length > field?.max) {
        return true;
      }
      return null;
    }
  },
  [RADIO_FIELD]: {
    validator: NO_VALIDATOR,
  }
}

function FeedbackForm({ handleSubmitClose }) {
  const feedbackFormConfig = useSelector((state) => state?.org?.customizations?.feedbackFormConfig);
  const profile = useSelector((state) => state?.org?.profile);
  const endUser = useSelector((state) => state?.chat?.endUser);
  const chatRecordId = useSelector((state) => state?.chat?.chatRecordId);
  const forms = useSelector((state) => state?.form?.forms);
  
  const id = `${chatRecordId}-feedback`;
  const { fields, state } = forms?.[id] ?? {};

  const closeFeedbackRef = useRef(null);
  const closedRef = useRef(false);
  const openRef = useRef(null);

  const [errorState, setErrorState] = useState({});
  const [submitted, setSubmitted] = useState(false);
  
  const theme = useTheme();
  const dispatch = useDispatch();
  
  const chatActionFn = getChatActions(dispatch);
  const { localizeText } = useLocalization();
  const initializeForm = () => {
    if(feedbackFormConfig?.fields?.length === fields?.length) return;
    dispatch(setFormFields(
      id,
      feedbackFormConfig?.fields,
      endUser
    ))
  }
  
  useEffect(initializeForm, [feedbackFormConfig?.fields]);

  const handleChange = (value, field) => {
    dispatch(setFormState(id, { [field]: value }));
  };

  const validateField = () => {
    let errors = {};
    for (const field of fields) {
      const fieldType = field?.type?.toLowerCase();
      const validator = FIELDS[fieldType]?.validator || NO_VALIDATOR;
      if (field?.required && field?.render && state?.[field?.field]?.length === 0) {
        if (fieldType === TEXTAREA_FIELD) {
          errors[field?.field] = localizeText(field?.errorMsg)?.replace("{{min}}", field?.min)?.replace("{{max}}", field?.max);
        }
        else {
          errors[field?.field] = localizeText(field?.errorMsg);
        }
      }

      if (state?.[field?.field]?.length > 0) {
        const error = validator(state?.[field?.field], field);
        if (error) {
          if (fieldType === TEXTAREA_FIELD) {
            errors[field?.field] = localizeText(field?.errorMsg)?.replace("{{min}}", field?.min)?.replace("{{max}}", field?.max);
          }
          else {
            errors[field?.field] = localizeText(field?.errorMsg);
          }
        }
      }
    }
    return errors;
  }

  const logEvent = (eventType, subtype) => {
    dispatch(logEventRecord(
      "",
      null,
      uuid(),
      eventType,
      subtype,
      true,
      ""
    ));
  };

  const handleClose = () => {
    if (closedRef.current) return;
    closedRef.current = true;
    handleSubmitClose(FEEDBACK_SUBMIT);
  };

  const handleGoBack = (e) => {
    e.preventDefault();
    if (closeFeedbackRef.current) {
      clearTimeout(closeFeedbackRef.current);
      closeFeedbackRef.current = null;
    }
    handleClose();
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    const errors = validateField();
    if (Object.keys(errors).length > 0) {
      console.log("Errors: ", errors);
      
      setErrorState(errors);
      return;
    }

    const formData = {
      ...state,
      endUser: {
        firstName: state?.firstName ?? endUser?.firstName,
        lastName: state?.lastName ?? endUser?.lastName,
        email: state?.email ?? endUser?.email,
        personas: endUser?.personas ?? []
      },
      profile: profile,
      feedbackId: chatRecordId,
    }

    delete formData["firstName"];
    delete formData["lastName"];
    delete formData["email"];

    chatActionFn.submitFeedback(formData);
    logEvent(FEEDBACK_SUBMIT, FEEDBACK_SUB_EVENT);
    setSubmitted(true);
    // close feedback form after 5 seconds if not already closed
    closeFeedbackRef.current = setTimeout(() => {
      closeFeedbackRef.current = null;
      handleClose();
    }, 5_000);
  };
  const handleEscape = () => {
    handleSubmitClose(FEEDBACK_CLOSE);
    document.getElementById("feedback-container")?.focus();
  }

  const handleOverlayKeyDown = useFocusTrap(openRef, handleEscape);


  const focusFeedbackForm = () => {
    if(openRef.current){
    const current = openRef.current;
    const focusable = current.querySelector(
      'input[type="text"], input[type="email"], textarea'
    );

    if (focusable) focusable.focus();

    current.addEventListener("keydown", handleOverlayKeyDown);

    // Return cleanup function
    return () => {
      current.removeEventListener("keydown", handleOverlayKeyDown);
    };
  }
  };

  useEffect(focusFeedbackForm, [openRef]);

  const handleKeyDown = (option, field) => {
    return (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleChange(localizeText(option), field?.field);
      }
    };
  };


  return (
    <Container ref={openRef}>
      <Form onSubmit={handleSubmit}>
        <StyledTypography variant="h2" component="h2" fontWeight={"600"} fontSize={theme.xxxxl} >{localizeText(feedbackFormConfig?.header)}</StyledTypography>
        <StyledTypography variant="p" component="p" fontWeight={"300"}>{localizeText(feedbackFormConfig?.title)}</StyledTypography>
        <RowWrapper>
          {!submitted && fields?.map((field, index) => {
            const ftype = field?.type?.toLowerCase();
            switch(ftype) {
              case EMAIL_FIELD:
              case STRING_FIELD:
                if(field?.render) {
                  return (
                    <Label>
                      <StyledTextField
                        label={localizeText(field?.placeholder)}
                        InputLabelProps={{ shrink: true }}
                        variant="outlined"
                        type={ftype === EMAIL_FIELD ? "email" : "text"}
                        disabled={endUser?.[field?.field] || !!field?.disabled}
                        required={!!field?.required}
                        value={state?.[field?.field]}
                        onChange={(e) => handleChange(e.target.value, field?.field)}
                        placeholder={localizeText(field?.placeholder)}
                        error={errorState?.[field?.field]?.length > 0}
                        helperText={errorState?.[field?.field] || ""}
                        sx={{ mt: 1 }}
                        inputProps={{
                          autoComplete:
                            field?.field === "firstName"
                              ? "given-name"
                              : field?.field === "lastName"
                              ? "family-name"
                              : field?.field === "email"
                              ? "email"
                              : "off",
                        }}
                      />
                    </Label>
                  )
                }
                break;
              case TEXTAREA_FIELD:
                if(field?.render) {
                  return (
                    <Label>
                      <StyledTextField
                        label={localizeText(field?.label)}
                        InputLabelProps={{ shrink: true }}
                        multiline
                        rows={4}
                        value={state?.[field?.field]}
                        required={!!field?.required}
                        onChange={(e) => handleChange(e.target.value, field?.field)}
                        disabled={!!field?.disabled}
                        placeholder={localizeText(field?.placeholder)}
                        sx={{ mt: 1 }}
                        error={errorState?.[field?.field]?.length > 0}
                        helperText={errorState?.[field?.field] || ""}
                        aria-label={localizeText(field?.placeholder)}
                        aria-live="assertive"
                      />
                    </Label>
                  )
                }
                break;
              case RADIO_FIELD:
                if(field?.render) {
                  if(field?.field?.toLowerCase() === "rating") {
                    return (
                      <Box sx={{ width: "100%" }} key={index}>
                        <StyledTypography variant="p" component="p" mt={2} id="rating-label">{localizeText(feedbackFormConfig?.rating?.label)}</StyledTypography>
                        <SmileyRatings onChange={handleChange} formState={state} data={field} ariaLabelledby="rating-label"/>
                      </Box>
                    )
                  }
                  return (
                    <FormControl>
                      {field?.label && <StyledTypography variant="p" component="p" id="product-feedback-radio-buttons">{localizeText(field?.label)}</StyledTypography>}
                      <RadioGroup
                        row
                        aria-labelledby="product-feedback-radio-buttons"
                        name="productFeedback"
                        value={state?.[field?.field]}
                        onChange={(e) => handleChange(e.target.value, field?.field)}
                      >
                        {field?.options?.map((option, index) => (
                          <StyledFormControlLabel required={!!field?.required} value={localizeText(option)} 
                            control={
                            <Radio  sx={{
                                '&:hover': {
                                  backgroundColor: theme.darkGrey,
                                },
                                '&.Mui-focusVisible': {
                                  backgroundColor: theme.darkGrey,
                                }
                              }}
                            />} 
                            label={localizeText(option)} 
                            key={index} 
                            onKeyDown={handleKeyDown(option, field)}
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>
                  )
                }
                break;
              default:
                return null;
            }
          })}
          {submitted && (
            <Box role="alert" aria-live="assertive" sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", rowGap: theme.md, margin: `${theme.xxxxl} auto` }}>
              <CheckCircleOutline sx={{ fontSize: "30%", color: theme.green, height: "60%", width: "60%" }} />
              <StyledTypography variant="p" component="p" fontWeight={"300"} fontSize={theme.xxxl} textAlign={"center"}>{localizeText(feedbackFormConfig?.thankYouMessage)}</StyledTypography>
            </Box>
          )}
        </RowWrapper>
        {!submitted ? 
          <StyledSubmitButton type="submit">Submit</StyledSubmitButton> : 
          <StyledSubmitButton onClick={handleGoBack} type="button">Go Back</StyledSubmitButton>
        }
      </Form>
    </Container>
  );
}


const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow-y: auto;
  overflow-x: hidden;
  margin: 1rem auto;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  margin: 1rem auto;
  box-sizing: border-box;
  font-family: ${({ theme }) => theme.fontFamily} !important;

  & input, & textarea, & select,
  & input:disabled, & textarea:disabled, & select:disabled 
  & {
    font-size: ${({ theme }) => theme.md};
    font-family: ${({ theme }) => theme.fontFamily} !important;
  }

  & input::placeholder, & textarea::placeholder, & select::placeholder {
    color: ${({ theme }) => theme.mediumGrey};
    font-size: ${({ theme }) => theme.md};
    user-select: none;
  }
  
  & .Mui-checked {
    color: ${({ theme }) => theme.primaryColor} !important;
  }
`;

const RowWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
`;

const StyledTypography = styled(Typography)(({ theme }) => ({
  fontFamily: theme.fontFamily,
  fontWeight: "300",
  fontSize: theme.md,
}));

const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
  fontFamily: theme.fontFamily,
  fontWeight: "300",
  fontSize: theme.md,

  "& .MuiTypography-root": {
    fontFamily: theme.fontFamily,
    fontWeight: "300",
    fontSize: theme.md,
  }
}))

const Label = styled.label`
  display: flex;
  flex-direction: column;
  min-width: 180px; /* so the label won't shrink below 180px */
  flex: 1 1 auto;
  font-size: ${({ theme }) => theme.md};
`;

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginTop: "5px",
  fontFamily: theme.fontFamily,
  "& .MuiOutlinedInput-root": {
    fontSize: theme.md,
    borderRadius: theme.xl,

    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.darkGrey,
    },
  },
  "& .MuiOutlinedInput-input": {
    padding: "8px",
  },
  "& .MuiInputBase-input::placeholder": {
    color: theme.veryDarkGrey,
    opacity: 1,
  }
}));


const StyledButton = styled.button`
  align-self: center;
  display: inline-flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  padding: ${({ theme }) => `${theme.xs} ${theme.md}`};
  color: white;
  font-family: ${({ theme }) => theme.fontFamily};
  font-size: ${({ theme }) => theme.md};
  line-height: 1.2;
  border: none;
  outline: none;
  box-shadow: none;
  text-transform: none;
  border-radius: ${({ theme }) => theme.xl};
  cursor: pointer;
  backface-visibility: hidden;
  transition: background-color 150ms ease-out;
`;

export const StyledSubmitButton = styled(StyledButton)`
  background-color: ${({ theme }) => theme.primaryColor};
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.primaryColor};
    outline-offset: 2px;
  }
  &:hover {
    background-color: ${({ theme }) => pSBC(0.05, theme.primaryColor)};
  }
`;

export default FeedbackForm;